import styled from 'styled-components';

import { COLORS, STATUS_COLORS, DESKTOP } from '../../../../theme/tokens';
import { Text } from '../../../../components';
import { hoverWash } from '@/nova/theme/mixins';

/**
 * Body of the "Password" panel: one line of copy above the change-password
 * button. Sits under its SectionTitle.
 */
export const PasswordBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 4px;
`;

/** Explanatory line above the change-password button. */
export const PasswordCopy = styled(Text)`
  display: block;
  font-size: 14px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** The sessions list inside the account panel. */
export const List = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
`;

/** Hairline between adjacent session rows. */
export const Divider = styled.div`
  height: 1px;
  background: ${COLORS.hairline};
`;

/** Approximate-location disclaimer under the list. */
export const Note = styled(Text)`
  display: block;
  margin-top: 10px;
  font-size: 12px;
  line-height: 1.5;
  color: ${COLORS.textFaint};
`;

/**
 * Footer action stack (sign out · log out everywhere). Full-width on mobile; from
 * desktop up a hairline separates it from the list above and the buttons shrink to
 * half-width, centred (the `&&` doubles specificity to beat the buttons' own
 * `width: 100%`).
 */
export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;

  ${DESKTOP} {
    align-items: center;
    border-top: 1px solid ${COLORS.hairline};
    padding-top: 20px;

    && > * {
      width: 50%;
    }
  }
`;

/**
 * Full-width "Log out of all devices" control. A danger-tinted sibling to the
 * primary Sign-out `Button`, matched to its 48px height so they stack evenly.
 * Page-local (not a shared variant) since it only exists here.
 */
export const AllDevicesButton = styled.button`
  appearance: none;
  cursor: pointer;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${hoverWash}
  box-sizing: border-box;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 18px;
  border-radius: 99px;
  font-size: 16px;
  color: ${STATUS_COLORS.negative};
  background: transparent;
  border: 1px solid ${COLORS.statusDanger};
  transition:
    background 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    background: ${COLORS.hoverWash};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;
